import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { productKeys } from '~/hooks/useProducts';
import * as projectsApi from '~/services/api/projects';
import { useSSEStore } from '~/stores/sseStore';

import type { CreateProjectPayload, ProjectWithTasks, UpdateProjectPayload } from '~/types';

import { useAuth } from './useAuth';
import { useTenant } from './useTenant';

export function useProject(id: string | null): {
  error: unknown;
  isLoading: boolean;
  project: ProjectWithTasks | null;
} {
  const { loading, user } = useAuth();
  const { isTenantReady } = useTenant();
  const {
    data: project,
    error,
    isPending,
  } = useQuery({
    queryKey: ['project', id],
    queryFn: () => (id ? projectsApi.getProject(id) : null),
    enabled: !loading && !!user && !!id && !id.startsWith('temp-') && isTenantReady,
    staleTime: 5 * 60 * 1000, // Data stays fresh for 5 minutes - prevents spam refetches
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
    refetchOnWindowFocus: false, // Don't auto-refetch - user can manually refresh
    refetchOnMount: false, // Don't refetch on component mount
    refetchOnReconnect: false, // Don't refetch when reconnecting
  });

  // isPending (not TanStack's isLoading) — isLoading is false while the query
  // is merely disabled (e.g. isTenantReady hasn't resolved yet), which made
  // ProjectView's `!project` branch flash "Project not found" before the
  // fetch had even started. isPending stays true until the query has
  // actually settled with data or an error, whether or not it was disabled
  // along the way.
  return {
    project: project || null,
    isLoading: isPending,
    error,
  };
}

export function useProjects(): {
  createProject: ReturnType<typeof useMutation<ProjectWithTasks, Error, CreateProjectPayload>>;
  deleteProject: ReturnType<
    typeof useMutation<void, Error, string, { previousProjects: ProjectWithTasks[] }>
  >;
  error: unknown;
  isLoading: boolean;
  projects: ProjectWithTasks[];
  updateProject: ReturnType<
    typeof useMutation<
      ProjectWithTasks,
      Error,
      { id: string; updates: UpdateProjectPayload },
      { previousProject: ProjectWithTasks | undefined; previousProjects: ProjectWithTasks[] }
    >
  >;
} {
  const { loading, user } = useAuth();
  const { isTenantReady } = useTenant();
  const queryClient = useQueryClient();

  const {
    data: projects = [],
    error,
    isLoading,
  } = useQuery({
    queryKey: ['projects'],
    queryFn: () => projectsApi.getProjects(),
    enabled: !loading && !!user && isTenantReady,
    staleTime: 5 * 60 * 1000, // Data stays fresh for 5 minutes - prevents spam refetches
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
    refetchOnWindowFocus: false, // Don't auto-refetch
    refetchOnMount: false, // Don't refetch on component mount
    refetchOnReconnect: false, // Don't refetch when reconnecting
    // NO refetchInterval - user can manually refresh via RefreshButton
  });

  const createProject = useMutation({
    mutationKey: ['projects', 'create'],
    mutationFn: projectsApi.createProject,
    onSuccess: (newProject): void => {
      queryClient.setQueryData(['projects'], (old: ProjectWithTasks[] = []) => [
        newProject,
        ...old,
      ]);
    },
  });

  const updateProject = useMutation({
    mutationKey: ['projects', 'update'],
    mutationFn: ({ id, updates }: { id: string; updates: UpdateProjectPayload }) =>
      projectsApi.updateProject(id, updates),
    // Optimistically apply the update to both the list cache (['projects'],
    // e.g. the ProjectCard status dropdown) and the single-project cache
    // (['project', id], e.g. ProjectView's header ProjectStatusSelect) so
    // every controlled UI reading either query reflects the change
    // immediately instead of staying on the stale server value until the
    // PATCH round-trip resolves. ADR-4: single-project cache optimism.
    onMutate: async ({ id, updates }) => {
      await queryClient.cancelQueries({ queryKey: ['projects'] });
      await queryClient.cancelQueries({ queryKey: ['project', id] });

      const previousProjects = queryClient.getQueryData<ProjectWithTasks[]>(['projects']) || [];
      const previousProject = queryClient.getQueryData<ProjectWithTasks>(['project', id]);

      queryClient.setQueryData<ProjectWithTasks[]>(['projects'], (old = []) =>
        old.map(project => (project.id === id ? { ...project, ...updates } : project)),
      );
      queryClient.setQueryData<ProjectWithTasks | undefined>(['project', id], old =>
        old ? { ...old, ...updates } : old,
      );

      return { previousProjects, previousProject };
    },
    onError: (_err, { id }, context): void => {
      // Roll back both optimistic updates if the request fails.
      if (context?.previousProjects) {
        queryClient.setQueryData(['projects'], context.previousProjects);
      }
      if (context?.previousProject) {
        queryClient.setQueryData(['project', id], context.previousProject);
      }
    },
    onSuccess: (updatedProject): void => {
      // Merge API response on top of cached project, preserving relationship
      // fields that the PATCH endpoint may not return (e.g. productId is absent
      // from the Project Pydantic model, and repositoryId can be nulled by
      // RLS/lazy-load issues during the ORM flush).
      const safeMerge = (cached: ProjectWithTasks): ProjectWithTasks => ({
        ...cached,
        ...updatedProject,
        repositoryId: updatedProject.repositoryId ?? cached.repositoryId,
        repository: updatedProject.repository ?? cached.repository,
        productId: updatedProject.productId ?? cached.productId,
        totalTasks: updatedProject.totalTasks ?? cached.totalTasks,
        completedTasks: updatedProject.completedTasks ?? cached.completedTasks,
      });
      queryClient.setQueryData(['projects'], (old: ProjectWithTasks[] = []) =>
        old.map(project => (project.id === updatedProject.id ? safeMerge(project) : project)),
      );
      queryClient.setQueryData(
        ['project', updatedProject.id],
        (old: ProjectWithTasks | undefined) => (old ? safeMerge(old) : updatedProject),
      );
      // Keep any product-scoped project boards (e.g. ProductView's kanban board,
      // which reads productKeys.projects(id)) in sync with this update.
      void queryClient.invalidateQueries({ queryKey: productKeys.all });
    },
  });

  const deleteProject = useMutation({
    mutationKey: ['projects', 'delete'],
    mutationFn: projectsApi.deleteProject,
    onMutate: async id => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['projects'] });
      await queryClient.cancelQueries({ queryKey: ['tasks', id] });

      // Snapshot the previous value
      const previousProjects = queryClient.getQueryData<ProjectWithTasks[]>(['projects']) || [];

      // Optimistically update to the new value
      queryClient.setQueryData(['projects'], (old: ProjectWithTasks[] = []) =>
        old.filter(project => project.id !== id),
      );

      // Clear tasks for this project
      queryClient.setQueryData(['tasks', id], []);

      // Clear SSE events for this project (prevent stale events in localStorage)
      useSSEStore.getState().clearProjectEvents(id);

      // Return a context object with the snapshotted value
      return { previousProjects };
    },
    onError: (_err, _id, context): void => {
      // If the mutation fails, use the context returned from onMutate to roll back
      // DON'T invalidate/refetch on error - we already rolled back and refetch will just fail again
      if (context?.previousProjects) {
        queryClient.setQueryData(['projects'], context.previousProjects);
      }
    },
    onSuccess: (): void => {
      // Only invalidate on SUCCESS to sync with server
      void queryClient.invalidateQueries({ queryKey: ['projects'] });
      // Keep any product-scoped project boards (e.g. ProductView's kanban board,
      // which reads productKeys.projects(id)) in sync with this deletion.
      void queryClient.invalidateQueries({ queryKey: productKeys.all });
    },
  });

  return {
    projects,
    isLoading,
    error,
    createProject,
    updateProject,
    deleteProject,
  };
}
